import { useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'pt-theme';

// SiteHeader mounts a `ThemeToggle` both next to the desktop nav/mobile
// hamburger and inside the mobile drawer, so two instances can be mounted at
// once. Each keeps its own React state (for the icon), so a click on one
// broadcasts this event to keep every mounted instance's icon in sync — the
// theme itself is already global via the shared `data-theme` DOM attribute.
const THEME_CHANGE_EVENT = 'pt-theme-change';

const readCurrentTheme = (): Theme =>
  document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';

/**
 * Subtle, icon-only light/dark toggle matching the mockup's `.themebtn`:
 * muted `text-ink-soft`/`opacity-70` at rest, full `text-ink`/`opacity-100`
 * on hover, a ~17px sun/moon glyph, no background/border/label — but a full
 * 44px tap target for touch. Shows the icon of the theme you'd switch TO
 * (moon while light, sun while dark).
 *
 * The theme itself is already applied before first paint by the inline head
 * script in app/views/layouts/application.html.erb (reads
 * `localStorage['pt-theme']`, defaults to 'light'). This component only
 * *reads* that already-applied `data-theme` (via a `useState` initializer) to
 * pick its initial icon — it must never re-derive the default or write to
 * storage on mount, or it would override a persisted choice / cause a flash.
 */
export const ThemeToggle = () => {
  const [theme, setTheme] = useState<Theme>(() =>
    typeof document === 'undefined' ? 'light' : readCurrentTheme(),
  );

  // Subscribe to `THEME_CHANGE_EVENT` once mounted, so this instance's icon
  // stays correct if a *different* mounted `ThemeToggle` (header vs. drawer)
  // is the one that gets clicked — this is the one legitimate use of an
  // effect here: syncing from an external system's events, not re-deriving
  // state React already has. It never writes localStorage or re-derives the
  // 'light' default itself, so a persisted 'dark' choice is never overridden.
  useEffect(() => {
    const onThemeChange = (event: Event) => {
      setTheme((event as CustomEvent<Theme>).detail);
    };
    window.addEventListener(THEME_CHANGE_EVENT, onThemeChange);
    return () => window.removeEventListener(THEME_CHANGE_EVENT, onThemeChange);
  }, []);

  const toDark = theme !== 'dark';

  const handleClick = () => {
    const next: Theme = document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = next;
    localStorage.setItem(THEME_STORAGE_KEY, next);
    setTheme(next);
    window.dispatchEvent(new CustomEvent<Theme>(THEME_CHANGE_EVENT, { detail: next }));
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={toDark ? 'Switch to dark theme' : 'Switch to light theme'}
      title="Toggle theme"
      className="text-ink-soft hover:text-ink grid size-11 shrink-0 place-items-center bg-transparent opacity-70 transition-[opacity,color] duration-150 hover:opacity-100"
    >
      {toDark ? (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          className="size-[17px]"
          aria-hidden="true"
        >
          <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          className="size-[17px]"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="4.2" />
          <path
            d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
};
